import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Briefcase,
  DollarSign,
  Clock,
  Ruler,
  ChevronRight,
  Users,
  Building2,
  Settings,
  Download,
  Loader2,
  TrendingUp,
  Shield,
  Wrench,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { api } from '../../api/client';
import { PageHeaderBand, type PageHeaderStat } from '../../components/layout/PageHeaderBand';
import { fmtCurrency } from '../../lib/display-labels';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  METRIC_DEFS, readRate, describeRate, numeratorPhrase, denominatorPhrase,
} from '../../lib/safety-metrics';
import { formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleLabel } from '../../lib/roles';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { jobStatusLabel } from '../../lib/job-status';
import { equipmentStatusLabel } from '../../lib/equipment-status';
import { CHART_INCIDENT_INK, CHART_SERIES_INK, jobStatusSlice } from '../../lib/report-chart-colours';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
  type LoadFailureKind,
} from '../../lib/load-failure';

type TimePeriod = 'week' | 'month' | 'ytd';
type AnalyticsTab = 'overview' | 'revenue' | 'customers' | 'crew' | 'invoices' | 'jobs' | 'safety' | 'equipment';


/**
 * Whole-dollar money for the dashboard tiles. Delegates the absent/real-zero
 * decision to the shared helper and only chooses the precision — the local body
 * tested `isNaN(Number(v))`, and Number(null) is 0, so an absent figure rendered
 * '$0' rather than '\u2014'. The precision is now an argument rather than a
 * reason to keep a copy.
 */
const fmtMoney = (v: number | string | null | undefined) => fmtCurrency(v, null, 0);

/**
 * Chart axis label for a month bucket.
 *
 * `month` is `date_trunc('month', t.work_date)` and `date_trunc('month',
 * si.incident_date)` — analytics.service.ts:23 and :306 — over DATE columns,
 * returned raw with no to_char. So it arrives as '2026-08-01T00:00:00.000Z',
 * and `new Date()` on that in Alberta is 18:00 on July 31: every revenue bar
 * and every incident bar was labelled with the PREVIOUS month.
 *
 * formatCalendarDate takes the date-part prefix and builds at local midnight,
 * so the bucket is labelled with its own month in any zone.
 */
function fmtMonth(dateStr: string): string {
  return formatCalendarDate(dateStr, { month: 'short', year: '2-digit' }) ?? '—';
}

// ─── Shared UI Components ────────────────────────────────────────────────────

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext: string;
  trend?: string;
  trendUp?: boolean;
  loading?: boolean;
}

/**
 * A body figure on the house card.
 *
 * THE FIGURE USED TO BE THE PLAIN WHITE-TEXT UTILITY. That one happens to work
 * — theme.css remaps its exact selector to the heading colour under the light
 * attribute, for the 958 elements that need it — but the house spelling for a
 * figure on a theme-following card is the ink token, and the remap is left to
 * the sites that have no token to reach for.
 *
 * `loading` renders a pulse, never a zero: a figure still in flight and a
 * figure that came back zero are different facts.
 */
const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, subtext, trend, trendUp, loading }) => (
  // `role="group"` + the label as the accessible name: the card is a figure and
  // its caption, and without a name on the container the only way to address one
  // is to find its label text and walk up to a parent — which is `no-node-access`
  // and, worse, breaks the moment the markup gains a wrapper. Naming the
  // container is the repo's answer to that (fe#761); it also means a screen
  // reader announces which figure it is about to read.
  <div role="group" aria-label={label} className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
    <div className="shrink-0 rounded-fi-control bg-fi-amber/10 p-2">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{label}</p>
      {loading ? (
        <span
          role="status"
          aria-label={`${label} loading`}
          className="mt-1.5 block h-6 w-20 animate-pulse rounded-fi-control bg-fieldiq-border"
        />
      ) : (
        <>
          <p className="mt-1 font-display text-xl font-extrabold tracking-tight text-fieldiq-text">{value}</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-xs text-fieldiq-muted">{subtext}</p>
            {trend && (
              <span className={`text-xs ${trendUp ? 'text-fieldiq-success' : 'text-fieldiq-danger'}`}>
                {trend}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  </div>
);

/**
 * A body panel in the register idiom — eyebrow, heading, content, and an
 * optional edge-to-edge footer action.
 *
 * `rail` is the ONE amber 4px left edge, and it belongs to one panel per view:
 * the register the reader came for. The charts and the secondary tables beside
 * it are plain panels.
 *
 * THE FOOTER SLOT CARRIES AN ACTION AND NEVER A CAPTION. The sentence fe#711
 * removed everywhere — a note about which side of the wire produced a figure —
 * is a rule for the person writing the screen, not something an office reader
 * can do anything with.
 */
const Panel: React.FC<{
  eyebrow: string;
  title: string;
  icon?: React.ReactNode;
  rail?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ eyebrow, title, icon, rail = false, children, footer }) => (
  <section
    className={`overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${
      rail ? 'border-l-4 border-l-fi-amber' : ''
    }`}
  >
    <header className="border-b border-fieldiq-border px-4 py-4">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
      <h2 className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
        {icon}
        {title}
      </h2>
    </header>
    <div className="px-4 py-4">{children}</div>
    {footer}
  </section>
);

/** A panel surface with no header, for a failure notice that must not change the panel's shape. */
const ReportCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${className}`}>
    {children}
  </div>
);

/**
 * THE HOVER INK IS THE TEXT TOKEN, NOT THE PLAIN WHITE UTILITY.
 *
 * theme.css remaps the exact white-text selector under the light attribute and
 * inherently does NOT claim its hover variant — that is a different class
 * token. So a white hover ink on a white card is white on white: the class is
 * in the DOM, the build is clean, and the word is simply not there while the
 * pointer is on it. Same repair fe#717 made on the report runner.
 */
const ReportButton: React.FC<{ children: React.ReactNode; onClick?: () => void }> = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="flex w-full items-center justify-center gap-2 border-t border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm font-semibold text-fieldiq-muted transition-colors hover:bg-fieldiq-border/40 hover:text-fieldiq-text"
  >
    {children}
    <ChevronRight className="w-4 h-4" />
  </button>
);

const DataRow: React.FC<{
  label: string;
  value: string;
  subvalue?: string;
  highlight?: boolean;
}> = ({ label, value, subvalue, highlight }) => (
  <div className="flex items-center justify-between border-b border-fieldiq-border py-3 last:border-0">
    <div>
      <p className={`font-medium ${highlight ? 'text-fieldiq-text' : 'text-fieldiq-muted'}`}>{label}</p>
      {subvalue && <p className="mt-0.5 text-xs text-fieldiq-muted">{subvalue}</p>}
    </div>
    <p className={`font-semibold ${highlight ? 'text-fieldiq-accent' : 'text-fieldiq-text'}`}>{value}</p>
  </div>
);

const LoadingSpinner: React.FC = () => (
  <div className="flex justify-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-fieldiq-accent" />
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-12 text-center text-sm text-fieldiq-muted">{message}</div>
);

/** One spelling for the heading and body rows of the six registers on this page. */
const TH_BASE = 'py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted';
const TABLE_HEAD_ROW = 'border-b border-fieldiq-border';
const TABLE_ROW = 'border-b border-fieldiq-border/50 transition-colors last:border-0 hover:bg-fieldiq-dark/50';

// ─── The third branch ────────────────────────────────────────────────────────

/**
 * A REFUSED TAB ON THIS PAGE USED TO READ "No revenue data available".
 *
 * Every tab below was `{ data, isLoading }` with no third branch, which by
 * construction has only two: a 403 arrives as `data === undefined`, the `if
 * (!data)` catches it, and the reader is told their company has no revenue, no
 * clients, no crew and no invoices. That is the AlertsScreen failure — the
 * screen answering a question it could not ask — seven times on one page.
 *
 * It was latent until fe#720, which linked this page from the reports catalog
 * on a row open to every role. Nobody without permission could reach the page
 * before; now a field worker lands here, and four of the eight tabs refuse
 * them. Linking it did not create the bug, it raised the price of it.
 *
 * The reasoning, the refusal/unreachable split and the wording all live in
 * `lib/load-failure.ts`; this is only the placement. It goes INSIDE a
 * ReportCard so the panel does not change shape between states, and it renders
 * exactly where the empty state would have been — not as a banner above one,
 * which would leave "No revenue data available" on screen underneath the
 * apology.
 */

/**
 * WHO THE API LETS THROUGH, READ OFF `reports.routes.ts` RATHER THAN GUESSED.
 *
 * `allowed` becomes "Admins and office staff can see crew productivity", so a
 * wrong list here sends the reader to argue with the wrong person. The gates
 * are non-strict, so an elevated dispatcher passes via 'admin' and simply does
 * not see a refusal at all.
 *
 * THE OPERATIONAL TABS DELIBERATELY PASS NO `allowed`. `OPERATIONAL_READ` is
 * `noRoleGate(...)` — it refuses nobody. A 401 can still reach those queries
 * when the token is rejected for that call, and naming a role list there would
 * describe a gate that does not exist: the reader's remedy is to sign in
 * again, not to ask an admin for a permission nobody has.
 */
const FINANCIAL_ALLOWED = 'Admins, office staff, supervisors and dispatchers';
const PAY_ALLOWED = 'Admins and office staff';
/**
 * `CREW_SCHEDULE_READ` happens to list the same four roles as `FINANCIAL_READ`
 * today, and this is a separate constant rather than a reuse because they are
 * separate gates in the route file. Folding them into one name here would make
 * a future divergence on the API side invisible on this screen.
 */
const CREW_SCHEDULE_ALLOWED = 'Admins, office staff, supervisors and dispatchers';

/** The shape `queryLoadFailure` reads — an unsettled query keeps its error elsewhere. */
type FailableQuery = { error?: unknown; fetchStatus?: string; failureReason?: unknown };

const TabLoadFailure: React.FC<{
  query: FailableQuery;
  kind: LoadFailureKind;
  subject: string;
  /** Omitted on an ungated endpoint — see the docblock above. */
  allowed?: string;
}> = ({ query, kind, subject, allowed }) => {
  const { user } = useAuth();
  return (
    <ReportCard>
      <LoadFailureNotice
        error={queryFailureError(query)}
        kind={kind}
        subject={subject}
        allowed={allowed}
        roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
      />
    </ReportCard>
  );
};

/**
 * One query, with the options a three-state screen needs.
 *
 * `LOAD_FAILURE_QUERY_OPTIONS` is not decoration and is the half that gets
 * dropped: without `networkMode: 'always'` a query that fails and wants to
 * retry parks in `paused` — error null, data undefined, isLoading FALSE — so
 * the failure branch never renders and `!data` sends the reader back to the
 * empty state. It bites the 5xx and the dead zone and NOT the 403, so a screen
 * checked only against a refusal looks finished while being unfixed for every
 * other failure.
 *
 * `queryLoadFailure` rather than `error` alone for the same reason: it reads
 * `failureReason` when the query is between attempts.
 */
function useReportQuery<T>(
  queryKey: unknown[],
  queryFn: () => Promise<T>,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    queryKey,
    queryFn,
    enabled: options?.enabled ?? true,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  return { query, failure: queryLoadFailure(query), data: query.data, isLoading: query.isLoading };
}

/**
 * A PRE-FORMATTED FIGURE THE API BUILT, OR AN EM DASH. NEVER `|| '$0'`.
 *
 * These overview panels render strings the API already formatted ('$12,480',
 * '14h', '1,240m'), so there is nothing here to run through `fmtCurrency`.
 * What they did instead was `financialData?.revenue || '$0'`, which is the
 * silent-blank-field bug in its money spelling: a figure that never arrived
 * renders as a confident zero, indistinguishable from a tenant who genuinely
 * billed nothing, and nobody reports it because from the outside nothing went
 * wrong.
 *
 * `||` and not `??` was the whole defect — the API's own '$0' is a real
 * answer and must survive, and it is falsy. This tests for absence only.
 */
const orDash = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/**
 * The job status pie's legend, drawn as HTML beside the chart rather than by
 * recharts. recharts' legend takes each swatch colour from the slice's `fill`
 * prop, which is `currentColor` now, so its swatches would all come out in the
 * legend's own text colour. This one takes the same status fill the rest of
 * the app uses, from the same map as the slice.
 */
const StatusLegend: React.FC<{ items: { status: string; name: string; value: number; swatch: string }[] }> = ({ items }) => (
  <ul aria-label="Status key" className="flex flex-wrap justify-center gap-x-4 gap-y-2 md:flex-col md:justify-start">
    {items.map((item) => (
      <li key={item.status} className="flex items-center gap-2 text-sm text-fieldiq-text">
        <span aria-hidden="true" className={`inline-block size-3 shrink-0 rounded-sm ${item.swatch}`} />
        <span>{item.name}</span>
        <span className="text-fieldiq-muted">{displayValue(item.value)}</span>
      </li>
    ))}
  </ul>
);

/**
 * THE CHART INK, WHICH WAS FOUR HARDCODED DARK HEXES AND IS NOW THE THEME'S.
 *
 * The tooltip was a `#1e2a3a` card with `#e2e8f0` ink — correct in the dark
 * half and a dark grey box floating over a white page in the light one. The
 * background rules were `#2d3748` and the axis tick labels `#a0aec0`, the latter
 * measuring about 2.3 on the white card: a chart with no readable scale in
 * light mode. Nothing reported any of it, because a colour written as a prop is
 * not a class and no remap can reach it.
 *
 * `var()` IS USED FOR THE TOOLTIP AND `currentColor` FOR THE CHART, AND THE
 * SPLIT IS NOT A PREFERENCE. `contentStyle` lands on a div's inline style,
 * where a custom property resolves normally. The axis and rule colours land on
 * SVG PRESENTATION ATTRIBUTES, where Chrome does not resolve `var()` at all —
 * `currentColor` it does, so each chart carries the ink class and the SVG
 * inherits it. recharts derives a tick LABEL's fill from the axis `stroke`
 * prop (CartesianAxis.js:304), so one `currentColor` covers the axis line and
 * its numbers together.
 *
 * The background rules take the same ink at 35%, which is what `#2d3748` was
 * against the dark surface and now is against either.
 */
const ChartTooltipStyle = {
  contentStyle: {
    background: 'var(--fieldiq-card)',
    border: '1px solid var(--fieldiq-border)',
    borderRadius: '8px',
    color: 'var(--fieldiq-text)',
  },
  labelStyle: { color: 'var(--fieldiq-muted)' },
};

/** The ink every chart on this page inherits through `currentColor`. */
const CHART_INK = 'text-fieldiq-muted';

// ─── Analytics Tab: Revenue ──────────────────────────────────────────────────

const RevenueTab: React.FC<{ months: number }> = ({ months }) => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'revenue', months],
    () => api.reports.revenueAnalytics(months).then(r => r.data.data),
  );

  if (failure) {
    return <TabLoadFailure query={query} kind={failure} subject="revenue analytics" allowed={FINANCIAL_ALLOWED} />;
  }
  if (isLoading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No revenue data available" />;

  const monthlyChart = (data.monthly || []).map((m: any) => ({
    name: fmtMonth(m.month),
    revenue: Number(m.revenue),
    jobs: m.job_count,
  }));

  const topCustomerChart = (data.topCustomers || []).map((c: any) => ({
    name: c.customer_name?.length > 20 ? c.customer_name.slice(0, 20) + '...' : c.customer_name,
    revenue: Number(c.revenue),
  }));

  const totals = data.totals || {};
  const momChange = totals.mom_change || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MetricCard
          icon={<DollarSign className="w-5 h-5 text-fieldiq-accent" />}
          label="Total Revenue"
          value={fmtMoney(totals.total_revenue)}
          subtext={`Last ${months} months`}
        />
        <MetricCard
          icon={<Briefcase className="w-5 h-5 text-fieldiq-accent" />}
          label="Avg Per Job"
          value={fmtMoney(totals.avg_per_job)}
          subtext={`${totals.total_jobs || 0} jobs`}
        />
        <MetricCard
          icon={<TrendingUp className="w-5 h-5 text-fieldiq-accent" />}
          label="MoM Change"
          value={`${momChange > 0 ? '+' : ''}${momChange}%`}
          subtext="Month over month"
          trend={momChange > 0 ? 'Up' : momChange < 0 ? 'Down' : 'Flat'}
          trendUp={momChange > 0}
        />
      </div>

      <Panel eyebrow="Revenue analytics" title="Monthly Revenue" rail>
        {monthlyChart.length > 0 ? (
          <ResponsiveContainer className={CHART_INK} width="100%" height={300}>
            <LineChart data={monthlyChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis dataKey="name" stroke="currentColor" fontSize={12} />
              <YAxis stroke="currentColor" fontSize={12} tickFormatter={(v) => fmtMoney(v)} />
              <Tooltip {...ChartTooltipStyle} formatter={(v: any) => [fmtMoney(v), 'Revenue']} />
              <Line className={CHART_SERIES_INK} type="monotone" dataKey="revenue" stroke="currentColor" strokeWidth={2} dot={{ className: CHART_SERIES_INK, fill: 'currentColor' }} activeDot={{ className: CHART_SERIES_INK, fill: 'currentColor' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No monthly data yet" />}
      </Panel>

      <Panel eyebrow="Revenue analytics" title="Top Clients by Revenue">
        {topCustomerChart.length > 0 ? (
          <ResponsiveContainer className={CHART_INK} width="100%" height={Math.max(200, topCustomerChart.length * 40)}>
            <BarChart data={topCustomerChart} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis type="number" stroke="currentColor" fontSize={12} tickFormatter={(v) => fmtMoney(v)} />
              <YAxis type="category" dataKey="name" stroke="currentColor" fontSize={11} width={140} />
              <Tooltip {...ChartTooltipStyle} formatter={(v: any) => [fmtMoney(v), 'Revenue']} />
              <Bar className={CHART_SERIES_INK} dataKey="revenue" fill="currentColor" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No customer data yet" />}
      </Panel>
    </div>
  );
};

// ─── Analytics Tab: Customer Profitability ────────────────────────────────────

const CustomersTab: React.FC<{ months: number }> = ({ months }) => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'customers', months],
    () => api.reports.customerProfitability(months).then(r => r.data.data),
  );

  if (failure) {
    return <TabLoadFailure query={query} kind={failure} subject="client profitability" allowed={FINANCIAL_ALLOWED} />;
  }
  if (isLoading) return <LoadingSpinner />;
  if (!data?.customers?.length) return <EmptyState message="No customer data available" />;

  return (
    <Panel eyebrow="Commercial pipeline" title="Client Profitability" rail>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={TABLE_HEAD_ROW}>
              <th className={`${TH_BASE} pr-4 text-left`}>Client</th>
              <th className={`${TH_BASE} px-2 text-right`}>Revenue</th>
              <th className={`${TH_BASE} px-2 text-right`}>Jobs</th>
              <th className={`${TH_BASE} px-2 text-right`}>Tickets</th>
              <th className={`${TH_BASE} pl-2 text-right`}>Avg/Job</th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((c: any) => (
              <tr key={c.customer_id} className={TABLE_ROW}>
                <td className="py-3 pr-4 font-medium text-fieldiq-text">{c.customer_name}</td>
                <td className="py-3 px-2 text-right text-fieldiq-accent font-semibold">{fmtMoney(c.total_revenue)}</td>
                <td className="py-3 px-2 text-right text-fieldiq-muted">{c.job_count}</td>
                <td className="py-3 px-2 text-right text-fieldiq-muted">{c.ticket_count}</td>
                <td className="py-3 pl-2 text-right text-fieldiq-text">{fmtMoney(c.avg_revenue_per_job)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// ─── Analytics Tab: Crew Productivity ─────────────────────────────────────────

const CrewTab: React.FC<{ months: number }> = ({ months }) => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'crew', months],
    () => api.reports.crewProductivity(months).then(r => r.data.data),
  );

  if (failure) {
    return <TabLoadFailure query={query} kind={failure} subject="crew productivity" allowed={PAY_ALLOWED} />;
  }
  if (isLoading) return <LoadingSpinner />;
  if (!data?.crew?.length) return <EmptyState message="No crew productivity data available" />;

  return (
    <Panel eyebrow="Crew" title="Crew Productivity" rail>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={TABLE_HEAD_ROW}>
              <th className={`${TH_BASE} pr-4 text-left`}>Employee</th>
              <th className={`${TH_BASE} px-2 text-right`}>Tickets</th>
              <th className={`${TH_BASE} px-2 text-right`}>Revenue Generated</th>
              <th className={`${TH_BASE} pl-2 text-right`}>Total Pay</th>
            </tr>
          </thead>
          <tbody>
            {data.crew.map((emp: any) => (
              <tr key={emp.user_id} className={TABLE_ROW}>
                <td className="py-3 pr-4 font-medium text-fieldiq-text">
                  <span className="inline-block w-8 h-8 rounded-full bg-fieldiq-accent/20 text-fieldiq-accent text-xs font-bold leading-8 text-center mr-2">
                    {emp.initials || '?'}
                  </span>
                  {emp.first_name} {emp.last_name}
                </td>
                <td className="py-3 px-2 text-right text-fieldiq-muted">{emp.tickets_worked}</td>
                <td className="py-3 px-2 text-right text-fieldiq-accent font-semibold">{fmtMoney(emp.revenue_generated)}</td>
                <td className="py-3 pl-2 text-right text-fieldiq-text">{fmtMoney(emp.total_pay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// ─── Analytics Tab: Invoice Aging ─────────────────────────────────────────────

const BUCKET_LABELS: Record<string, string> = {
  'current': 'Current',
  '1-30': '1-30 days',
  '31-60': '31-60 days',
  '61-90': '61-90 days',
  '90+': '90+ days',
};

const InvoicesTab: React.FC = () => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'invoice-aging'],
    () => api.reports.invoiceAging().then(r => r.data.data),
  );

  if (failure) {
    return <TabLoadFailure query={query} kind={failure} subject="invoice aging" allowed={FINANCIAL_ALLOWED} />;
  }
  if (isLoading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No invoice data available" />;

  const bucketChart = (data.buckets || []).map((b: any) => ({
    name: BUCKET_LABELS[b.bucket] || b.bucket,
    total: Number(b.total),
    count: b.count,
  }));

  const totals = data.totals || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MetricCard
          icon={<DollarSign className="w-5 h-5 text-fieldiq-accent" />}
          label="Total Outstanding"
          value={fmtMoney(totals.total_outstanding)}
          subtext="All open invoices"
        />
        <MetricCard
          icon={<AlertTriangle className="w-5 h-5 text-fieldiq-accent" />}
          label="Total Overdue"
          value={fmtMoney(totals.total_overdue)}
          subtext="Past due date"
        />
      </div>

      <Panel eyebrow="Receivables" title="Aging Buckets" rail>
        {bucketChart.length > 0 ? (
          <ResponsiveContainer className={CHART_INK} width="100%" height={250}>
            <BarChart data={bucketChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis dataKey="name" stroke="currentColor" fontSize={12} />
              <YAxis stroke="currentColor" fontSize={12} tickFormatter={(v) => fmtMoney(v)} />
              <Tooltip {...ChartTooltipStyle} formatter={(v: any) => [fmtMoney(v), 'Amount']} />
              {/* One hue: the axis names each bucket, so colour would only
                  restate the label, in the status colours' vocabulary. */}
              <Bar className={CHART_SERIES_INK} dataKey="total" fill="currentColor" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No outstanding invoices" />}
      </Panel>

      {data.overdueInvoices?.length > 0 && (
        <Panel eyebrow="Receivables" title="Top Overdue Invoices">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  <th className={`${TH_BASE} pr-4 text-left`}>Invoice</th>
                  <th className={`${TH_BASE} px-2 text-left`}>Client</th>
                  <th className={`${TH_BASE} px-2 text-right`}>Balance Due</th>
                  <th className={`${TH_BASE} pl-2 text-right`}>Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {data.overdueInvoices.map((inv: any) => (
                  <tr key={inv.id} className={TABLE_ROW}>
                    <td className="py-3 pr-4 text-fieldiq-accent font-medium">{inv.invoice_number}</td>
                    <td className="py-3 px-2 text-fieldiq-muted">{inv.customer_name}</td>
                    <td className="py-3 px-2 text-right font-semibold text-fieldiq-text">{fmtMoney(inv.balance_due)}</td>
                    <td className="py-3 pl-2 text-right">
                      <span className={`font-semibold ${inv.days_overdue > 60 ? 'text-red-400' : inv.days_overdue > 30 ? 'text-orange-400' : 'text-fieldiq-text'}`}>
                        {inv.days_overdue}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
};

// ─── Analytics Tab: Job Completion ────────────────────────────────────────────

const JobsTab: React.FC<{ months: number }> = ({ months }) => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'jobs', months],
    () => api.reports.jobCompletion(months).then(r => r.data.data),
  );

  // No `allowed`: job completion is ungated, so a refusal here is a rejected
  // token and not a role the reader lacks.
  if (failure) return <TabLoadFailure query={query} kind={failure} subject="job completion figures" />;
  if (isLoading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No job data available" />;

  const pieData = (data.statusBreakdown || []).map((s: any) => ({
    status: s.status,
    name: jobStatusLabel(s.status),
    value: s.count,
    ...jobStatusSlice(s.status),
  }));

  const stats = data.stats || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MetricCard
          icon={<CheckCircle className="w-5 h-5 text-fieldiq-accent" />}
          label="Completion Rate"
          value={`${stats.completion_rate || 0}%`}
          subtext={`${stats.completed_jobs || 0} of ${stats.total_jobs || 0} jobs`}
        />
        <MetricCard
          icon={<Clock className="w-5 h-5 text-fieldiq-accent" />}
          label="Avg Days to Complete"
          value={`${stats.avg_days_to_complete || 0}`}
          subtext="From scheduled date"
        />
        <MetricCard
          icon={<Briefcase className="w-5 h-5 text-fieldiq-accent" />}
          label="Total Jobs"
          value={String(stats.total_jobs || 0)}
          subtext={`Last ${months} months`}
        />
      </div>

      <Panel eyebrow="Job completion" title="Status Breakdown" rail>
        {pieData.length > 0 ? (
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            {/* A fixed width for the pie so the key sits beside it rather than
                at the far edge of a full-width chart box. */}
            <div className="w-full md:w-[320px] md:shrink-0">
              <ResponsiveContainer className={CHART_INK} width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry: any, idx: number) => (
                      <Cell key={idx} className={entry.ink} fill="currentColor" />
                    ))}
                  </Pie>
                  <Tooltip {...ChartTooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <StatusLegend items={pieData} />
          </div>
        ) : <EmptyState message="No job status data" />}
      </Panel>
    </div>
  );
};

// ─── Analytics Tab: Safety ────────────────────────────────────────────────────

const SafetyTab: React.FC = () => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'safety'],
    () => api.reports.safetyAnalytics().then(r => r.data.data),
  );

  /* No `allowed`: safety analytics is ungated — it is the row fe#720 opened to
   * every role — so a refusal is a rejected token, not a missing permission.
   * And this is the AlertsScreen tab of this page: a safety screen that cannot
   * reach the server must not draw a TRIR, a clean incident chart, or any
   * other reassuring figure over a failed load. */
  if (failure) return <TabLoadFailure query={query} kind={failure} subject="safety analytics" />;
  if (isLoading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No safety data available" />;

  const incidentsByMonth = (data.incidentsByMonth || []).map((m: any) => ({
    name: fmtMonth(m.month),
    incidents: m.count,
  }));

  const byType = (data.incidentsByType || []).map((t: any) => ({
    name: `${t.incident_type.replace('_', ' ')} (${t.severity_level})`,
    count: t.count,
  }));

  const attendance = data.meetingAttendance || {};
  /**
   * `null` when the figure never arrived; a number (including a real 0) when
   * it did.
   *
   * The null/undefined test comes FIRST and is not folded into the isFinite
   * check, because `Number(null)` is **0** — the absent case would have
   * arrived as the same value as the genuine no-meetings case, and the card
   * would say "No meetings recorded" about data it never received. Two facts,
   * one value, which is the collision this file has three other instances of.
   */
  const rawRecords = attendance.total_records;
  const attendanceRecords: number | null =
    rawRecords === null || rawRecords === undefined || rawRecords === '' || !Number.isFinite(Number(rawRecords))
      ? null
      : Number(rawRecords);

  /* TRIR, read the way the metrics page reads every rate. The reason copy comes
   * from the shared map so this card and /safety/metrics cannot describe the
   * same absent figure two different ways.
   *
   * `describeRate` wants a MetricDef; the analytics payload is the TRIF rate
   * flattened onto sibling keys, so it is reassembled into a Rate here rather
   * than re-deriving the wording. Nothing is recomputed — every figure below is
   * displayed as the API sent it. */
  const trifDef = METRIC_DEFS.find((d) => d.key === 'trif')!;
  const trirRate = readRate({
    value: data.trir ?? null,
    numerator: data.ytdIncidents ?? 0,
    denominator: data.ytdHours ?? null,
    reason: data.trirReason ?? null,
  });
  const trirDisplay = describeRate(trirRate, trifDef);
  const trir = trirDisplay.kind === 'value' ? trirRate.value : null;
  const trirReason = trirDisplay.kind === 'missing' ? trirDisplay.reason : '';
  const trirNumerator = numeratorPhrase(trirRate, trifDef);
  const trirDenominator = denominatorPhrase(trirRate, trifDef) ?? '—';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* `data.trir` is `number | null` and the null is the answer, not a gap
          * to fill. This card used to read `String(data.trir || 0)`, which drew
          * a confident "0" for every tenant that had never entered an exposure
          * hour — the same figure a company with a genuine zero would show, on
          * a number that goes on prequalification packages.
          *
          * `?? null` and an explicit isFinite test, NOT `||`: a real TRIR of 0
          * is an earned result and must render as "0.00" at full strength. */}
        <MetricCard
          icon={<Shield className="w-5 h-5 text-fieldiq-accent" />}
          label="TRIR"
          value={trir === null ? '—' : trir.toFixed(2)}
          subtext={trir === null ? trirReason : `YTD · ${trirNumerator} / ${trirDenominator}`}
        />
        {/* THE API CAN NOW SAY "NEVER", AND THIS CARD SAYS IT (api#798).
          *
          * `daysSinceLastIncident` used to be `0` for two opposite facts:
          *
          *   - an incident happened TODAY  — the worst reading this card has
          *   - this tenant has NEVER filed one — the best
          *
          * `analytics.service.ts` spelled the "never" branch with the same
          * value the "today" branch produces, so the client was handed one
          * number and could not tell which it meant — the
          * sentinel-that-is-also-a-legal-value shape from CLAUDE.md, arriving
          * over the wire. The fix belonged in the API, and that is where it
          * landed: `daysSinceLastIncident` is now `number | null`, `null`
          * meaning no incident is on file.
          *
          * `== null`, NOT `||`. A genuine 0 — an incident TODAY — is the most
          * important number this card can show and must survive at full
          * strength; `|| 0` and `?? 0` would each put the two states back
          * together, which is the bug this replaced.
          *
          * AND THE ABSENT CASE IS NOT AN EM DASH ON ITS OWN. A bare "—" reads
          * as "we could not get this figure" — the em-dash rule's meaning
          * everywhere else on this page — where the truth here is a fact about
          * the COMPANY: nothing has ever been filed. The sub-line is what
          * separates them, per fe#702: an empty set is a fact about the
          * subject, an absent value is a fact about the data, and they never
          * get one spelling.
          *
          * A failed or refused READ still arrives as `undefined`, and that is
          * also `== null` here. It is the same em dash with the same sub-line,
          * which is a known and accepted imprecision on this card: the query's
          * own failure branch is what reports a refusal, above.
          */}
        <MetricCard
          icon={<CheckCircle className="w-5 h-5 text-fieldiq-accent" />}
          label="Days Since Last Incident"
          value={data.daysSinceLastIncident == null ? '\u2014' : String(data.daysSinceLastIncident)}
          subtext={data.daysSinceLastIncident == null ? 'No incidents recorded' : ''}
        />
        {/* 0 OF 0 IS NOT A RATE, AND THE API HANDS YOU ONE.
          *
          * The attendance query is an aggregate, so it returns a row whatever
          * happens, and its rate is `CASE WHEN COUNT(*) > 0 THEN ... ELSE 0`
          * (analytics.service.ts:394). A tenant that has never held a safety
          * meeting therefore gets `{attendance_rate: 0, present_count: 0,
          * total_records: 0}`, and this card drew a confident **0%** over
          * "0 / 0 records" — a number that reads as "nobody turns up to our
          * safety meetings" on a card that goes in front of prequalification
          * auditors, produced by a company that has simply not logged any.
          *
          * `total_records` settles it on the client with no recomputation:
          * the denominator is either there or it is not, and the rate is only
          * a rate when it is. A genuine 0% — nobody present at 50 logged
          * meetings — is an earned figure and still renders at full strength,
          * which is why this tests the DENOMINATOR and not the rate.
          *
          * The two absences are kept apart, per fe#702: no meetings on file is
          * a fact about the company, an unreturned figure is a fact about the
          * data, and one spelling for both is the bug this page is full of. */}
        <MetricCard
          icon={<Users className="w-5 h-5 text-fieldiq-accent" />}
          label="Meeting Attendance"
          value={attendanceRecords !== null && attendanceRecords > 0 ? `${attendance.attendance_rate}%` : '—'}
          subtext={
            attendanceRecords === null
              ? 'Attendance not recorded'
              : attendanceRecords === 0
                ? 'No meetings recorded'
                : `${attendance.present_count} / ${attendanceRecords} records`
          }
        />
      </div>

      <Panel eyebrow="Safety" title="Incidents by Month" rail>
        {incidentsByMonth.length > 0 ? (
          <ResponsiveContainer className={CHART_INK} width="100%" height={260}>
            <LineChart data={incidentsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis dataKey="name" stroke="currentColor" fontSize={12} />
              <YAxis stroke="currentColor" fontSize={12} allowDecimals={false} />
              <Tooltip {...ChartTooltipStyle} />
              <Line className={CHART_INCIDENT_INK} type="monotone" dataKey="incidents" stroke="currentColor" strokeWidth={2} dot={{ className: CHART_INCIDENT_INK, fill: 'currentColor' }} activeDot={{ className: CHART_INCIDENT_INK, fill: 'currentColor' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No incidents recorded" />}
      </Panel>

      {byType.length > 0 && (
        <Panel eyebrow="Safety" title="Incidents by Type">
          <ResponsiveContainer className={CHART_INK} width="100%" height={Math.max(200, byType.length * 40)}>
            <BarChart data={byType} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis type="number" stroke="currentColor" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="currentColor" fontSize={11} width={180} />
              <Tooltip {...ChartTooltipStyle} />
              <Bar className={CHART_INCIDENT_INK} dataKey="count" fill="currentColor" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}
    </div>
  );
};

// ─── Analytics Tab: Equipment Utilization ─────────────────────────────────────

const EquipmentTab: React.FC<{ months: number }> = ({ months }) => {
  const { query, failure, data, isLoading } = useReportQuery(
    ['analytics', 'equipment', months],
    () => api.reports.equipmentUtilization(months).then(r => r.data.data),
  );

  // No `allowed`: equipment utilization is ungated.
  if (failure) return <TabLoadFailure query={query} kind={failure} subject="equipment utilization" />;
  if (isLoading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No equipment data available" />;

  const statusBars = (data.statusBreakdown || []).map((s: any) => ({
    // The register's own label, so this bar and the register's pill agree.
    name: equipmentStatusLabel(s.status),
    value: s.count,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MetricCard
          icon={<Wrench className="w-5 h-5 text-fieldiq-accent" />}
          label="Active Equipment"
          value={String(data.equipment?.length || 0)}
          subtext="Non-retired units"
        />
        <MetricCard
          icon={<AlertTriangle className="w-5 h-5 text-fieldiq-accent" />}
          label="Overdue Inspections"
          value={String(data.overdueInspections || 0)}
          subtext="Past due date"
        />
      </div>

      {statusBars.length > 0 && (
        <Panel eyebrow="Fleet" title="Equipment by Status" rail>
          {/* Bars in one hue, each status named on the axis. The register
              colours these statuses with the signal hues, which are fills for
              dark ink and read 1.29-2.69 as a chart mark on the light card, so
              a pie in those colours cannot pass and a pie in any other colours
              would contradict the register. */}
          <ResponsiveContainer className={CHART_INK} width="100%" height={Math.max(200, statusBars.length * 40)}>
            <BarChart data={statusBars} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.35} />
              <XAxis type="number" stroke="currentColor" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="currentColor" fontSize={11} width={120} />
              <Tooltip {...ChartTooltipStyle} formatter={(v: any) => [v, 'Units']} />
              <Bar className={CHART_SERIES_INK} dataKey="value" fill="currentColor" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      {data.equipment?.length > 0 && (
        <Panel eyebrow="Fleet" title="Equipment Utilization">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  <th className={`${TH_BASE} pr-4 text-left`}>Equipment</th>
                  <th className={`${TH_BASE} px-2 text-left`}>Type</th>
                  <th className={`${TH_BASE} px-2 text-right`}>Jobs</th>
                  <th className={`${TH_BASE} px-2 text-right`}>Days Used</th>
                  <th className={`${TH_BASE} pl-2 text-right`}>Util %</th>
                </tr>
              </thead>
              <tbody>
                {data.equipment.map((eq: any) => (
                  <tr key={eq.id} className={TABLE_ROW}>
                    <td className="py-3 pr-4 font-medium text-fieldiq-text">{eq.serial_number || eq.name}</td>
                    <td className="py-3 px-2 text-fieldiq-muted">{eq.type}</td>
                    <td className="py-3 px-2 text-right text-fieldiq-muted">{eq.job_count}</td>
                    <td className="py-3 px-2 text-right text-fieldiq-muted">{eq.days_in_use}</td>
                    <td className="py-3 pl-2 text-right">
                      <span className={`font-semibold ${Number(eq.utilization_pct) > 60 ? 'text-green-400' : Number(eq.utilization_pct) > 30 ? 'text-fieldiq-text' : 'text-fieldiq-muted'}`}>
                        {eq.utilization_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

const TABS: { key: AnalyticsTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'customers', label: 'Clients' },
  { key: 'crew', label: 'Crew' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'safety', label: 'Safety' },
  { key: 'equipment', label: 'Equipment' },
];

/**
 * ── THE EIGHT TABS GO IN THE BODY, DIRECTLY UNDER THE BAND ──────────────────
 *
 * NEVER ON THE BAND. Everything drawn on `--fi-brand` has to be spelled in
 * constant white opacities, because the band stays navy in both themes — a
 * strip there would need a permanently-navy active/inactive treatment unlike
 * every other strip in the app, and the hero circle's clearance was swept
 * against STAT TEXT ONLY, so a strip on the band is a second thing crossing the
 * circle that nobody has measured. Settled by fe#712 and restated here because
 * this is the second page to carry both shapes.
 *
 * TABS AND NOT CHIPS, by fe#704's test — what SWITCHES. A chip narrows ONE
 * list: every chip asks the same question of the same rows, so each can carry a
 * count. These eight switch WHICH QUESTION the page answers — Revenue, Crew,
 * Invoices and Safety share no list, no scope, no controls and no endpoint —
 * and a count on any of them would be a count of a different dataset, which is
 * a count of nothing. So: underlined, unnumbered, and the period pills below
 * them are the filled pills.
 *
 * THE ACTIVE TAB IS NOT MARKED BY INK COLOUR. `--fieldiq-accent-signal` is 4.36
 * on `--fieldiq-dark` in light mode — under the floor on the one surface a body
 * strip sits on — so the amber underline and the weight carry the state and the
 * word stays the text token. The strip this replaces used the accent for both
 * the word and the underline.
 *
 * `aria-pressed` rather than the full tab/tablist/tabpanel pattern, matching
 * Timesheets: each tab is its own URL (`?tab=`), independently linkable, which
 * is a set of toggle buttons rather than a roving-tabindex widget over one
 * panel.
 *
 * The accessible name is the label and nothing else — no icon, no count — which
 * is what `ReportsDashboard.tab-deeplink.test.tsx` addresses its anchored
 * `/^Safety$/` query at.
 */
const TabStrip: React.FC<{ active: AnalyticsTab; onChange: (tab: AnalyticsTab) => void }> = ({ active, onChange }) => (
  <div
    data-testid="reports-tab-strip"
    role="group"
    aria-label="Switch report"
    className="scrollbar-hide flex gap-1 overflow-x-auto border-b border-fieldiq-border"
  >
    {TABS.map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onChange(tab.key)}
        aria-pressed={active === tab.key}
        className={`relative whitespace-nowrap px-3 py-2 text-sm transition-colors ${
          active === tab.key
            ? 'font-extrabold text-fieldiq-text'
            : 'font-semibold text-fieldiq-muted hover:text-fieldiq-text'
        }`}
      >
        {tab.label}
        {active === tab.key && (
          <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />
        )}
      </button>
    ))}
  </div>
);

/**
 * The filled pills in the body, one shape for both period controls. The active
 * one is an amber fill with dark ink — the badge idiom, which holds in both
 * themes — never amber INK, which has no light twin and reads 1.49 on the wash
 * it is normally paired with.
 */
const PeriodPills: React.FC<{
  label: string;
  options: { key: string; label: string }[];
  active: string;
  onPick: (key: string) => void;
}> = ({ label, options, active, onPick }) => (
  <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
    <span className="text-xs text-fieldiq-muted">{label}</span>
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        aria-pressed={active === o.key}
        onClick={() => onPick(o.key)}
        className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
          active === o.key
            ? 'bg-fi-amber text-fi-amber-ink'
            : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * The scope the band's two figures cover, in the page's own words.
 *
 * READ OFF `period`, WHICH IS CLIENT STATE, NOT OFF THE PAYLOAD'S `subtext`.
 * The API sends the same three strings, and the band would then lose its
 * sub-lines on exactly the loads where the figures above them went to an em
 * dash — the reader losing the scope at the moment the scope is the only thing
 * still knowable. It is a label, not a figure, so nothing is being recomputed.
 */
const PERIOD_SCOPE: Record<TimePeriod, string> = {
  week: 'This week',
  month: 'This month',
  ytd: 'Year to date',
};

const MONTH_OPTIONS = [3, 6, 12, 24].map((m) => ({ key: String(m), label: `${m}mo` }));
const PERIOD_OPTIONS: { key: TimePeriod; label: string }[] = [
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'ytd', label: 'YTD' },
];

/**
 * Is this string one of the eight tabs? Read off TABS rather than re-listing
 * the union, so a tab added above cannot be deep-linkable in the type and
 * unreachable by URL.
 *
 * An unrecognised `?tab=` falls back to Overview rather than rendering nothing:
 * a stale or mistyped link lands on a working page, not an empty one.
 */
const isAnalyticsTab = (value: string | null): value is AnalyticsTab =>
  !!value && TABS.some((t) => t.key === value);

export const ReportsDashboard: React.FC = () => {
  const navigate = useNavigate();

  /**
   * The tab lives in the URL, not in component state (fe#720).
   *
   * This page is reached from the reports catalog's Safety Analytics row, which
   * points at `/reports/dashboard?tab=safety` — the live safety endpoint has a
   * renderer here and nowhere else, and a row that landed on Overview would
   * leave the reader to go looking for it. Holding the tab in state and reading
   * the param once on mount would do the same job and then lie about it the
   * moment anyone copied the URL, so the param IS the state.
   *
   * `replace: true`: switching tabs on one page is not eight history entries,
   * and Back still leaves the dashboard the way the reader expects.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AnalyticsTab = isAnalyticsTab(tabParam) ? tabParam : 'overview';
  const setActiveTab = useCallback(
    (tab: AnalyticsTab) => {
      setSearchParams(
        (prev) => {
          // Rebuilt from `prev`, never replaced with a fresh object — this
          // route carries no other param today and that is not a reason to
          // write code that would drop one.
          const next = new URLSearchParams(prev);
          if (tab === 'overview') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { user } = useAuth();
  const roleLabel = user?.role ? getRoleLabel(user.role) : undefined;
  const timeZone = useTenantTimeZone();

  const [period, setPeriod] = useState<TimePeriod>('month');
  const [analyticMonths, setAnalyticMonths] = useState(12);

  /**
   * THE SEVEN OVERVIEW PANELS, EACH ANSWERING FOR ITSELF.
   *
   * This was one `Promise.all` into seven `useState`s, and `Promise.all`
   * REJECTS ON THE FIRST FAILURE. Two of the seven are gated and five are not,
   * so a field worker — who reaches this page now that fe#720 linked it —
   * refused on `/dashboard/financial` lost the whole tab: the five panels that
   * loaded perfectly well rendered as empty lists, and the two refused ones
   * rendered '$0'. One 403 erased six panels' worth of data it had nothing to
   * say about, and the only notice was a toast that is gone by the time anyone
   * reads the screen.
   *
   * Seven queries instead, because the honest answer differs per panel: the
   * ungated five show their data, and each refused panel says so in its own
   * card. That is the `kind`-per-source case LoadFailureNotice documents.
   *
   * `enabled` keeps the old fan-out condition — nothing is fetched until the
   * Overview tab is the one on screen — and `period` rides in the query key,
   * so switching period refetches exactly as the effect's dependency did.
   */
  const overview = { enabled: activeTab === 'overview' };

  /**
   * THE ONE QUERY THAT IS NOT TAB-SCOPED, AND THE ONLY NON-PRESENTATIONAL LINE
   * IN THIS RE-SKIN.
   *
   * `/reports/dashboard/metrics` answers for the COMPANY over the selected
   * period — a job count and a metres total — not for any one tab. The band
   * carries those two figures and the band is page-level, so the query has to
   * be too: with `enabled` still keyed to the Overview tab, the band would show
   * both figures on Overview and two em dashes on the other seven.
   *
   * THAT EM DASH WOULD BE A LIE, WHICH IS WHY THIS IS A CHANGE AND NOT A
   * RENDERING CHOICE. An em dash on this page means "the figure did not
   * arrive". "The page never asked" is a different fact, and spelling it with
   * the absent value is the unloaded-vs-empty collision CLAUDE.md records three
   * other instances of in this file. The honest options were to ask on every
   * tab or to carry no figures at all; this asks.
   *
   * IT IS SAFE TO ASK ON EVERY TAB because `OPERATIONAL_READ` is `noRoleGate`
   * — read off `reports.routes.ts`, not assumed — so no role that can open this
   * page is refused it, and no viewer sees a stat they cannot read. It is also
   * why the six panels below keep their `overview` gate: each of those answers
   * for the Overview tab alone, and two of them are gated.
   *
   * The cost is one request on a tab that did not previously make it, against
   * the same cache key the Overview tab uses, so arriving on `?tab=safety` and
   * then clicking Overview now refetches nothing.
   */
  const metricsQ = useReportQuery(['reports', 'dashboard', 'metrics', period],
    () => api.reports.dashboardMetrics(period).then(r => r.data.data));
  const recentJobsQ = useReportQuery(['reports', 'dashboard', 'recent-jobs'],
    () => api.reports.dashboardRecentJobs().then(r => r.data.data), overview);
  const byCustomerQ = useReportQuery(['reports', 'dashboard', 'by-customer', period],
    () => api.reports.dashboardByCustomer(period).then(r => r.data.data), overview);
  const byWellQ = useReportQuery(['reports', 'dashboard', 'by-well'],
    () => api.reports.dashboardByWell().then(r => r.data.data), overview);
  const byEquipmentQ = useReportQuery(['reports', 'dashboard', 'by-equipment'],
    () => api.reports.dashboardByEquipment().then(r => r.data.data), overview);
  const financialQ = useReportQuery(['reports', 'dashboard', 'financial', period],
    () => api.reports.dashboardFinancial(period).then(r => r.data.data), overview);
  const payrollQ = useReportQuery(['reports', 'dashboard', 'payroll', period],
    () => api.reports.dashboardPayroll(period).then(r => r.data.data), overview);

  /**
   * THE OVERVIEW PANELS READ KEYS THE API HAS NEVER SENT, AND THE EM DASHES
   * THEY PRODUCE ARE CORRECT.
   *
   * Audited key by key against fieldiq-api `src/routes/reports.routes.ts` on
   * 2026-09-20 — every `/reports/dashboard/*` handler read, none inferred.
   * Each entry below renders an em dash or an empty cell today. None of them
   * is a defect to close by binding whatever key the API happens to send
   * nearest to it. See the money-column entry in CLAUDE.md.
   *
   * FOUR OF THEM ARE TRAPS and carry their own note at the site, because the
   * API's nearest key is a JOB COUNT, and a job count sitting in a money or
   * a detail column is a small plausible number that nothing downstream
   * checks:
   *
   *   customerData[].revenue          by-customer sends `job_count`
   *   wellData[].info                 by-well sends `job_count`
   *   equipmentData[].info            by-equipment sends `job_count`
   *   payrollData.employees[].amount  payroll sends `jobs_assigned`
   *
   * The rest are cosmetic, and are written down so the next reader does not
   * re-audit them:
   *
   *   metrics.revenue           /dashboard/metrics sends `jobs` and `metres`
   *   metrics.duration          only, each `{ value, subtext }`. No revenue
   *   metrics.jobs.trend        figure, no duration figure, and no trend is
   *   metrics.jobs.trendUp      computed on that route at all.
   *
   *   recentJobs[].customer     sent as `customer_name`
   *   recentJobs[].metres       not sent; the handler selects no metres
   *   recentJobs[].revenue      not sent; the handler selects no money
   *   recentJobs[].id           sent, but it is the job UUID. `job_number` is
   *                             what the "Job" heading promises and it rides
   *                             in the same row already.
   *
   *   financialData.revenue     /dashboard/financial sends `approved_tickets`
   *   financialData.gst         and `pending_tickets` and nothing else. No
   *   financialData.outstanding money is computed on that route.
   *
   *   payrollData.employees[].name  sent as `first_name` / `last_name` /
   *                                 `initials`; no joined name is returned
   *   payrollData.total             not sent. The nearest key is
   *                                 `active_employee_count`, a head count
   *
   *   data.daysSinceLastIncident    sent, and CORRECT since api#798 — `null`
   *                                 for "never", a number otherwise. It stays
   *                                 in this list as the one entry that got
   *                                 fixed rather than documented; see the note
   *                                 at that card in SafetyTab above
   *
   * Nothing here is repaired in this pass, deliberately. Every one is a
   * question the API owns, and a client-side guess at any of them is the
   * second answer to it.
   */
  const metrics = metricsQ.data as any;
  const recentJobs: any[] = recentJobsQ.data ?? [];
  const customerData: any[] = byCustomerQ.data ?? [];
  const wellData: any[] = byWellQ.data ?? [];
  const equipmentData: any[] = byEquipmentQ.data ?? [];
  const financialData = financialQ.data as any;
  const payrollData = payrollQ.data as any;

  // The TENANT's today (companies.time_zone), not the device's — the API
  // scopes the period in the tenant's zone, so the label has to agree with it.
  // No zone: an em dash rather than a guessed month.
  const getPeriodLabel = () => {
    const today = todayCalendarDate(timeZone);
    const month = formatCalendarDate(today, { month: 'short' }, 'en-US');
    const year = today?.slice(0, 4);
    const day = today ? Number(today.slice(8, 10)) : null;
    switch(period) {
      case 'week': return `This Week`;
      case 'month': return month && year ? `${month} ${year}` : '—';
      case 'ytd': return month && year && day ? `Jan 1 - ${month} ${day}, ${year}` : '—';
    }
  };

  /**
   * ── THE BAND CARRIES TWO FIGURES, NOT FOUR, AND THAT IS THE FINDING ────────
   *
   * The standard allows two to four. Four honest page-level stats that EVERY
   * role viewing this page can read do not exist here, and the fourth and third
   * would have had to be invented. The audit, endpoint by endpoint:
   *
   *   /dashboard/metrics    OPERATIONAL_READ (ungated). Page-scoped by
   *                         construction — it answers for the company over the
   *                         selected period. Returns `jobs` and `metres`, each
   *                         `{ value, subtext }`, AND NOTHING ELSE. These two.
   *
   *   /dashboard/financial  FINANCIAL_READ, and a field worker is refused it.
   *   /dashboard/payroll    CREW_SCHEDULE_READ, same. A stat either of these
   *                         fed would be an em dash for the very reader fe#720
   *                         opened this page to — a permanent gap that says
   *                         "absent" where the truth is "not yours".
   *
   *   /analytics/safety     ungated, but TAB-scoped: it is the Safety tab's
   *   /analytics/job-…      dataset, not the page's. Promoting one to fill a
   *   /analytics/equipment… slot would put three more requests on every visit
   *                         to buy a figure the band has no claim to. And the
   *                         one figure that looks page-level on the safety
   *                         payload, `daysSinceLastIncident`, is TAB-scoped
   *                         like the rest of that dataset. (api#798 made it
   *                         unambiguous — `null` is "never" — but ambiguity was
   *                         never why it could not go on the band, and the
   *                         scoping reason is unchanged.)
   *
   * So the honest set is the two `/dashboard/metrics` returns. CLAUDE.md's
   * ceiling on the third stat's label does not apply — there is no third
   * column — but the clearance sweep still does, and a two-column strip puts
   * the second label near the middle of the band rather than under the circle.
   * Swept at every 8px from 1024 to 2560 in both themes; readings in the PR.
   *
   * BOTH FIGURES COME FROM ONE QUERY, so they are absent together or present
   * together and the strip never shifts. `orDash` tests for absence only — the
   * API's own '0' is a real answer and must survive, and it is falsy.
   */
  const bandStats: PageHeaderStat[] = [
    {
      icon: <Briefcase className="size-4" aria-hidden="true" />,
      label: 'Jobs',
      sub: PERIOD_SCOPE[period],
      tone: 'accent',
      loading: metricsQ.isLoading,
      value: orDash(metrics?.jobs?.value),
    },
    {
      icon: <Ruler className="size-4" aria-hidden="true" />,
      label: 'Metres run',
      sub: PERIOD_SCOPE[period],
      tone: 'info',
      loading: metricsQ.isLoading,
      value: orDash(metrics?.metres?.value),
    },
  ];

  return (
    <div data-testid="reports-dashboard-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        title="Reports & Analytics"
        /* /reports, not navigate(-1). fe#725 repointed every report page at the
           catalog because history-back is not a destination: arriving from the
           catalog's Safety row sent Back to Safety, and a fresh load did
           nothing at all. */
        onBack={() => navigate('/reports')}
        backLabel="Back to reports"
        /* NO ACTIONS, and that is a clearance decision rather than a shortage
           of things to put here. fe#726 found that an extra band action or a
           wrapped title pushes the stat strip down and fails the circle's
           clearance even with every label inside the limit. Nothing on this
           page needs a band action: the tab strip switches the view and each
           panel carries its own. */
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <TabStrip active={activeTab} onChange={setActiveTab} />

          {/* The period controls, in the body under the strip. Two of them,
              because they narrow two different things and always did: Overview
              asks week/month/YTD of the dashboard endpoints, the analytics tabs
              ask a month count of theirs. Invoice aging and safety take neither,
              exactly as before. */}
          {activeTab === 'overview' && (
            <PeriodPills
              label={getPeriodLabel()}
              options={PERIOD_OPTIONS}
              active={period}
              onPick={(k) => setPeriod(k as TimePeriod)}
            />
          )}
          {activeTab !== 'overview' && activeTab !== 'invoices' && activeTab !== 'safety' && (
            <PeriodPills
              label="Period"
              options={MONTH_OPTIONS}
              active={String(analyticMonths)}
              onPick={(k) => setAnalyticMonths(Number(k))}
            />
          )}

          <div className="space-y-6">
            {/* ─── Overview Tab (existing content) ─── */}
            {activeTab === 'overview' && (
              <>
                {/* Ungated (OPERATIONAL_READ), so no `allowed` — a refusal here is
                    a rejected token, not a permission the reader is missing. */}
                {metricsQ.failure ? (
                  <TabLoadFailure query={metricsQ.query} kind={metricsQ.failure} subject="the month's figures" />
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <MetricCard
                      icon={<Briefcase className="w-5 h-5 text-fieldiq-accent" />}
                      label="Jobs This Month"
                      value={orDash(metrics?.jobs?.value)}
                      subtext={metrics?.jobs?.subtext || ''}
                      trend={metrics?.jobs?.trend}
                      trendUp={metrics?.jobs?.trendUp}
                      loading={metricsQ.isLoading}
                    />
                    <MetricCard
                      icon={<DollarSign className="w-5 h-5 text-fieldiq-accent" />}
                      label="Total Revenue"
                      value={orDash(metrics?.revenue?.value)}
                      subtext={metrics?.revenue?.subtext || ''}
                      loading={metricsQ.isLoading}
                    />
                    <MetricCard
                      icon={<Clock className="w-5 h-5 text-fieldiq-accent" />}
                      label="Avg Job Duration"
                      value={orDash(metrics?.duration?.value)}
                      subtext={metrics?.duration?.subtext || ''}
                      loading={metricsQ.isLoading}
                    />
                    <MetricCard
                      icon={<Ruler className="w-5 h-5 text-fieldiq-accent" />}
                      label="Metres Run"
                      value={orDash(metrics?.metres?.value)}
                      subtext={metrics?.metres?.subtext || ''}
                      loading={metricsQ.isLoading}
                    />
                  </div>
                )}

                <Panel
                  eyebrow="Operations"
                  title="Recent Jobs"
                  icon={<Briefcase className="w-5 h-5" />}
                  rail
                >
                    <div className="grid grid-cols-4 gap-2 text-xs text-fieldiq-muted uppercase tracking-wider mb-3">
                      <span>Job</span>
                      <span>Client</span>
                      <span className="text-right">Metres</span>
                      <span className="text-right">Revenue</span>
                    </div>
                    {recentJobsQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(recentJobsQ.query)}
                        kind={recentJobsQ.failure}
                        subject="recent jobs"
                      />
                    ) : recentJobsQ.isLoading ? (
                      <LoadingSpinner />
                    ) : recentJobs.length === 0 ? (
                      <EmptyState message="No jobs in this period" />
                    ) : (
                      recentJobs.map((job, idx) => (
                        <div
                          key={job.id}
                          className={`grid grid-cols-4 gap-2 py-3 text-sm ${
                            idx !== recentJobs.length - 1 ? 'border-b border-fieldiq-border' : ''
                          }`}
                        >
                          <span className="text-fieldiq-accent font-medium">{job.id}</span>
                          <span className="text-fieldiq-text">{job.customer}</span>
                          <span className="text-right text-fieldiq-text">{job.metres}</span>
                          <span className="text-right font-medium text-fieldiq-text">{job.revenue}</span>
                        </div>
                      ))
                    )}
                </Panel>

                <Panel
                  eyebrow="Operations"
                  title="By Client"
                  icon={<Building2 className="w-5 h-5" />}
                  footer={
                    <ReportButton>Run Full Report</ReportButton>
                  }
                >
                    {byCustomerQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(byCustomerQ.query)}
                        kind={byCustomerQ.failure}
                        subject="the client breakdown"
                      />
                    ) : byCustomerQ.isLoading ? (
                      <LoadingSpinner />
                    ) : customerData.length === 0 ? (
                      <EmptyState message="No clients in this period" />
                    ) : (
                      /* `revenue` HAS NEVER EXISTED ON THIS PAYLOAD. THE EM DASH
                       * IS RIGHT, AND THE OBVIOUS REPAIR IS WRONG.
                       *
                       * GET /reports/dashboard/by-customer returns
                       * `{ name, job_count }` and nothing else, at every period.
                       * There is no money anywhere on that handler.
                       *
                       * DO NOT REBIND THIS TO `job_count`. It is a COUNT OF
                       * JOBS. Under a heading a reader takes revenue off, it
                       * would print `7` where `$7,400` belongs — both are small
                       * positive numbers, so it looks like data, and no test,
                       * type or reviewer downstream can tell them apart. An em
                       * dash is the honest answer to a question this endpoint
                       * does not answer; a job count is a confident wrong one.
                       *
                       * The repair is an API that returns revenue, or a heading
                       * that asks for the job count it already has. Three
                       * sibling panels on this tab carry the identical trap.
                       */
                      customerData.map((customer) => (
                        <DataRow
                          key={customer.name}
                          label={customer.name}
                          value={customer.revenue}
                        />
                      ))
                    )}
                </Panel>

                <Panel
                  eyebrow="Operations"
                  title="By Well/Rig"
                  icon={<Settings className="w-5 h-5" />}
                  footer={
                    <ReportButton>Run Full Report</ReportButton>
                  }
                >
                    {byWellQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(byWellQ.query)}
                        kind={byWellQ.failure}
                        subject="the well breakdown"
                      />
                    ) : byWellQ.isLoading ? (
                      <LoadingSpinner />
                    ) : wellData.length === 0 ? (
                      <EmptyState message="No wells in this period" />
                    ) : (
                      /* `info` HAS NEVER EXISTED ON THIS PAYLOAD, and the em dash
                       * is the correct rendering of that.
                       *
                       * GET /reports/dashboard/by-well returns
                       * `{ name, job_count }` — well_name grouped, counted, top
                       * ten. Nothing that answers to the word "info".
                       *
                       * DO NOT REBIND THIS TO `job_count`. The column is free
                       * text beside a well name, so a bare `4` reads as whatever
                       * the reader expects to see there — metres, hours, a rig
                       * number, a dollar figure. The value carries no unit and
                       * the panel promises none, which is precisely why a wrong
                       * one here is unfalsifiable on screen.
                       *
                       * If the job count is what this panel should show, label
                       * it as a job count in the same change. Same trap as the
                       * By Client panel above.
                       */
                      wellData.map((well) => (
                        <DataRow
                          key={well.name}
                          label={well.name}
                          value={well.info}
                        />
                      ))
                    )}
                </Panel>

                <Panel
                  eyebrow="Operations"
                  title="By Equipment"
                  icon={<Settings className="w-5 h-5" />}
                  footer={
                    <ReportButton>Run Full Report</ReportButton>
                  }
                >
                    {byEquipmentQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(byEquipmentQ.query)}
                        kind={byEquipmentQ.failure}
                        subject="the equipment breakdown"
                      />
                    ) : byEquipmentQ.isLoading ? (
                      <LoadingSpinner />
                    ) : equipmentData.length === 0 ? (
                      <EmptyState message="No equipment in this period" />
                    ) : (
                      /* `info` HAS NEVER EXISTED ON THIS PAYLOAD.
                       *
                       * GET /reports/dashboard/by-equipment returns
                       * `{ name, type, status, job_count }`. Three real keys are
                       * sitting here unread, and `info` is not one of them.
                       *
                       * DO NOT REBIND THIS TO `job_count`. It is the number of
                       * LIVE jobs the asset is assigned to — a utilisation
                       * figure the API handler is careful about (it excludes
                       * soft-deleted jobs on purpose). Printed with no label it
                       * reads as an inventory number, an hour meter or a cost.
                       *
                       * `type` or `status` is the honest thing to show in a
                       * detail column beside an asset name, and both arrive
                       * already. Rendering either is a behaviour change and is
                       * out of scope here — but it is the change to make, not a
                       * rebind to the count. Same trap as the two panels above.
                       */
                      equipmentData.map((equip) => (
                        <DataRow
                          key={equip.name}
                          label={equip.name}
                          value={equip.info}
                        />
                      ))
                    )}
                </Panel>

                <Panel
                  eyebrow="Finance"
                  title="Financial Summary"
                  icon={<DollarSign className="w-5 h-5" />}
                  footer={
                    <ReportButton>Full Financial Report</ReportButton>
                  }
                >
                    {financialQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(financialQ.query)}
                        kind={financialQ.failure}
                        subject="the financial summary"
                        allowed={FINANCIAL_ALLOWED}
                        roleLabel={roleLabel}
                      />
                    ) : financialQ.isLoading ? (
                      <LoadingSpinner />
                    ) : (
                      /* THIS PANEL IS UNIMPLEMENTED API-SIDE, NOT UNPOPULATED.
                       * ALL THREE EM DASHES ARE CORRECT AND ARE NOT A BUG TO
                       * CLOSE.
                       *
                       * GET /reports/dashboard/financial exists, answers 200 and
                       * returns `{ approved_tickets, pending_tickets }`. It runs
                       * one COUNT over tickets. It computes no revenue, no GST
                       * and no receivable, for any tenant, in any period — so
                       * there is no tenant whose data would fill this in, and no
                       * amount of looking at production will produce one.
                       *
                       * Which means the failure here is NOT the em-dash rule
                       * misfiring. It is three headings written ahead of the
                       * endpoint. Turning them into `$0.00` would state that
                       * this company billed nothing, which is a claim the app
                       * has no basis for; turning them into the two ticket
                       * counts would answer a different question under a money
                       * heading.
                       *
                       * Either the API grows these three figures, or the panel
                       * shows the two counts it is actually sent under headings
                       * that name them. Both are behaviour changes and neither
                       * belongs in a docs pass.
                       */
                      <>
                        <DataRow label="Revenue" value={orDash(financialData?.revenue)} />
                        <DataRow label="GST Collected" value={orDash(financialData?.gst)} />
                        <DataRow label="Outstanding" value={orDash(financialData?.outstanding)} highlight />
                      </>
                    )}
                </Panel>

                <Panel
                  eyebrow="Crew"
                  title="Payroll Summary"
                  icon={<Users className="w-5 h-5" />}
                  footer={
                    <ReportButton>
                      <Download className="w-4 h-4" />
                      Export to QuickBooks
                    </ReportButton>
                  }
                >
                    {payrollQ.failure ? (
                      <LoadFailureNotice
                        error={queryFailureError(payrollQ.query)}
                        kind={payrollQ.failure}
                        subject="the payroll summary"
                        allowed={CREW_SCHEDULE_ALLOWED}
                        roleLabel={roleLabel}
                      />
                    ) : payrollQ.isLoading ? (
                      <LoadingSpinner />
                    ) : (
                      /* THIS PANEL IS UNIMPLEMENTED API-SIDE TOO, AND IT CARRIES
                       * THE SHARPEST OF THE FOUR COUNT TRAPS.
                       *
                       * GET /reports/dashboard/payroll returns
                       * `{ employees: [{ id, first_name, last_name, initials,
                       * jobs_assigned }], active_employee_count }`. No pay rate,
                       * no hours, no dollar figure of any kind is computed on
                       * that route for any tenant. The em dashes are correct.
                       *
                       * `employee.amount` DOES NOT EXIST. The nearest key is
                       * `jobs_assigned` — A COUNT OF JOBS. Under a payroll
                       * heading, next to a person's name, `11` reads as eleven
                       * hundred dollars or eleven hours to anyone who glances at
                       * it, and it is the one figure on this screen someone
                       * might repeat to the person it names.
                       *
                       * `employee.name` does not exist either: the row carries
                       * `first_name`, `last_name` and `initials` separately and
                       * this panel currently labels every row with nothing. That
                       * one is a real repair and is a behaviour change, so it is
                       * out of scope here.
                       *
                       * `payrollData.total` below is the same shape: not sent.
                       * The nearest key, `active_employee_count`, is a HEAD
                       * COUNT — binding it would print the crew size as the
                       * payroll total.
                       */
                      <>
                        {payrollData?.employees?.map((employee: any) => (
                          <DataRow
                            key={employee.name}
                            label={employee.name}
                            value={orDash(employee.amount)}
                          />
                        ))}
                        <div className="flex items-center justify-between py-3 border-t-2 border-fieldiq-border mt-2">
                          <span className="font-semibold text-fieldiq-text">Total Payroll</span>
                          <span className="text-fieldiq-accent font-bold text-lg">{orDash(payrollData?.total)}</span>
                        </div>
                      </>
                    )}
                </Panel>
              </>
            )}

            {/* ─── Analytics Tabs ─── */}
            {activeTab === 'revenue' && <RevenueTab months={analyticMonths} />}
            {activeTab === 'customers' && <CustomersTab months={analyticMonths} />}
            {activeTab === 'crew' && <CrewTab months={analyticMonths} />}
            {activeTab === 'invoices' && <InvoicesTab />}
            {activeTab === 'jobs' && <JobsTab months={analyticMonths} />}
            {activeTab === 'safety' && <SafetyTab />}
            {activeTab === 'equipment' && <EquipmentTab months={analyticMonths} />}
          </div>
        </div>
      </section>
    </div>
  );
};
